import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import classes from './stepper.module.css';

const Stepper = ({
  steps,
  currentStep,
  onStepClick,
  showNumbers = false,
  animated = true,
  clickable = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Calculate progress width for continuous line
  const progressWidth = ((currentStep + 1) / steps.length) * 100;
  
  // Add a small delay to make animation visible
  const [displayWidth, setDisplayWidth] = useState(0);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDisplayWidth(progressWidth);
    }, 100);
    return () => clearTimeout(timer);
  }, [progressWidth]);

  const handleStepClick = (step, index) => {
    if (!clickable) return;
    
    if (onStepClick) {
      onStepClick(step, index);
    } else {
      // Default navigation logic
      const pathParts = location.pathname.split('/');
      const idIndex = pathParts.findIndex((part) => part === 'proposal') + 1;
      const id = pathParts[idIndex] && pathParts[idIndex] !== step.path.split('/').pop() ? pathParts[idIndex] : null;
      
      if (id) {
        navigate(step.path.replace(':id', id));
      } else {
        navigate(step.path);
      }
    }
  };

  return (
    <div className={classes.stepperContainer}>
      <div className={classes.stepper}>
        {/* Continuous progress line */}
        <div 
          className={classes.progressLine}
          style={{ 
            width: `${displayWidth}%`
          }}
        />
        
        {steps.map((step, index) => (
          <div
            key={step.id}
            className={[
              classes.step,
              index === currentStep
                ? classes.activeStep
                : index < currentStep
                  ? classes.completedStep
                  : '',
              !clickable ? classes.viewOnly : '',
            ].filter(Boolean).join(' ')}
            onClick={() => handleStepClick(step, index)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleStepClick(step, index);
              }
            }}
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : undefined}
            aria-label={`${step.name} - שלב ${index + 1}`}
            aria-current={index === currentStep ? 'step' : undefined}
          >
            <div className={classes.stepContent}>
              {showNumbers && (
                <div className={classes.stepNumber}>{index + 1}</div>
              )}
              <div className={classes.stepTitle}>{step.name}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

Stepper.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
  })).isRequired,
  currentStep: PropTypes.number.isRequired,
  onStepClick: PropTypes.func,
  showNumbers: PropTypes.bool,
  animated: PropTypes.bool,
  clickable: PropTypes.bool,
};

export default Stepper; 
